import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchClients, fetchClientsIntegrationStatus, createClient } from "@/lib/api";
import {
  Plus, Search, ArrowUpRight, Check, BarChart3, Youtube, Instagram, Facebook, Linkedin,
  Music2, Twitter, Send, FileSpreadsheet, Megaphone, MousePointer, ClipboardCheck,
  Briefcase, User2, Code2, BookOpen,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const INTEGRATION_ICONS = {
  // Datos
  metricool: BarChart3,
  estado_situacion: ClipboardCheck,
  por_fuera: FileSpreadsheet,
  outreach: Send,
  // Redes
  youtube: Youtube,
  instagram: Instagram,
  facebook: Facebook,
  linkedin_corpo: Linkedin,
  linkedin_personal: User2,
  tiktok: Music2,
  twitter: Twitter,
  // Configuración
  bd_plan: Briefcase,
  cro: MousePointer,
  comms_plan: Megaphone,
  tech: Code2,
  notion: BookOpen,
};

const CATEGORY_ORDER = ["data", "social", "config"];
const CATEGORY_LABELS = {
  data: "Datos",
  social: "Redes",
  config: "Configuración",
};

export default function ClientsList() {
  const qc = useQueryClient();
  const { data: clients = [], isLoading } = useQuery({ queryKey: ["clients"], queryFn: fetchClients });
  const { data: statusList = [] } = useQuery({
    queryKey: ["clients-integration-status"],
    queryFn: fetchClientsIntegrationStatus,
    staleTime: 60 * 1000,
  });

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");

  const create = useMutation({
    mutationFn: () => createClient({ name, industry, color: "#2563EB" }),
    onSuccess: () => {
      toast.success("Client created");
      setOpen(false);
      setName("");
      setIndustry("");
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["clients-integration-status"] });
    },
    onError: () => toast.error("Failed to create client"),
  });

  const statusMap = Object.fromEntries(statusList.map((s) => [s.client_id, s]));
  const filtered = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(query.toLowerCase()) ||
      (c.industry || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="clients-search"
            placeholder="Search clients or industries…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-10 rounded-md bg-white border-slate-200"
          />
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn" className="h-10 rounded-md bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-1.5" /> New client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Create new client</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Add a client to your portfolio. Integrations will start disconnected.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-slate-500">Client name</Label>
                <Input id="name" data-testid="new-client-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="industry" className="text-xs uppercase tracking-wider text-slate-500">Industry</Label>
                <Input id="industry" data-testid="new-client-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                data-testid="new-client-submit"
                onClick={() => create.mutate()}
                disabled={!name || !industry || create.isPending}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {create.isPending ? "Creating…" : "Create client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-56 rounded-md skeleton" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <ClientCard key={c.id} client={c} status={statusMap[c.id]} />
          ))}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center text-sm text-slate-500">
          No clients match your search.
        </div>
      )}
    </div>
  );
}

function ClientCard({ client, status }) {
  const integrations = status?.integrations || [];
  const byCategory = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: integrations.filter((i) => i.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <Link
      to={`/clients/${client.id}`}
      data-testid={`client-card-${client.id}`}
      className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 hover:shadow-sm transition-all flex flex-col gap-4"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className="h-11 w-11 rounded-md flex items-center justify-center font-display font-semibold text-white shrink-0"
          style={{ background: client.color }}
        >
          {client.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 truncate">{client.name}</div>
          <div className="text-xs text-slate-500 truncate">{client.industry}</div>
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-400 shrink-0" />
      </div>

      {/* Integration badges grouped by category */}
      <div className="space-y-3 pt-3 border-t border-slate-100" data-testid={`client-integrations-${client.id}`}>
        {byCategory.map((g) => (
          <div key={g.cat}>
            <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1.5">
              {CATEGORY_LABELS[g.cat]}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {g.items.map((it) => (
                <IntegrationBadge key={it.key} item={it} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Link>
  );
}

function IntegrationBadge({ item }) {
  const Icon = INTEGRATION_ICONS[item.key] || Check;
  const connected = item.connected;
  return (
    <span
      data-testid={`integration-badge-${item.key}-${connected ? "on" : "off"}`}
      title={`${item.label}: ${connected ? "Conectado" : "Falta conectar"}`}
      className={`inline-flex items-center gap-1 text-[10.5px] font-medium ${
        connected ? "text-emerald-700" : "text-slate-400"
      }`}
    >
      <Icon className="h-3 w-3" />
      <span className={connected ? "" : "line-through decoration-slate-300 decoration-[1px]"}>
        {item.label}
      </span>
    </span>
  );
}
